import { useCallback, useState } from 'react';
import { Icon } from '@ascend/shared/components';

const STORAGE_VALUE = 'true';

export type MockDataDisclaimerProps = {
  /** Per-app key so admin and client remember acceptance separately. Bump suffix if copy changes require re-acknowledgment. */
  storageKey: string;
};

export function MockDataDisclaimer({ storageKey }: MockDataDisclaimerProps) {
  const [acknowledged, setAcknowledged] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(storageKey) === STORAGE_VALUE;
  });
  const [understood, setUnderstood] = useState(false);

  const accept = useCallback(() => {
    if (!understood) return;
    window.localStorage.setItem(storageKey, STORAGE_VALUE);
    setAcknowledged(true);
  }, [storageKey, understood]);

  return (
    <>
      {!acknowledged && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/55 p-4 backdrop-blur-[2px]"
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="mock-data-disclaimer-title"
            aria-describedby="mock-data-disclaimer-desc"
            className="max-h-[min(90vh,640px)] w-full max-w-lg overflow-y-auto rounded-xl border border-border bg-background p-6 shadow-lg"
          >
            <div className="flex gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400">
                <Icon name="streamline:warning-triangle" className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1 space-y-3">
                <h2
                  id="mock-data-disclaimer-title"
                  className="text-lg font-semibold leading-tight text-foreground"
                >
                  Simulated data only
                </h2>
                <p id="mock-data-disclaimer-desc" className="text-sm leading-relaxed text-muted-foreground">
                  This environment uses <strong className="font-medium text-foreground">mocked and simulated</strong>{' '}
                  data for demonstration. Figures, statuses, flows, and outcomes are illustrative only. A live system
                  may differ materially based on real data, configuration, market conditions, and operational scenarios.
                </p>
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-muted/40 p-3 text-sm leading-snug text-foreground">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-border accent-primary"
                    checked={understood}
                    onChange={(e) => setUnderstood(e.target.checked)}
                  />
                  <span>
                    I understand and accept that what I see here is not production data and may not reflect actual
                    behavior.
                  </span>
                </label>
                <button
                  type="button"
                  disabled={!understood}
                  onClick={accept}
                  className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {acknowledged && (
        <div
          className="pointer-events-none fixed bottom-4 left-1/2 z-[60] w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 px-2"
          role="status"
          aria-live="polite"
        >
          <div className="pointer-events-auto rounded-lg border border-amber-500/35 bg-background/95 px-4 py-2.5 text-center text-xs text-muted-foreground shadow-md backdrop-blur-sm supports-[backdrop-filter]:bg-background/80">
            <span className="font-medium text-amber-800 dark:text-amber-200">Mock / simulated data</span>
            <span className="mx-1.5 text-border">·</span>
            Not production; real systems may differ.
          </div>
        </div>
      )}
    </>
  );
}
